import { PillTheme } from "@redotech/redo-model/pill-theme";
import classNames from "classnames";
import { memo } from "react";
import {
  RedoBadge,
  RedoBadgeColor,
  RedoBadgeSize,
  RedoBadgeType,
} from "../arbiter-components/badge/redo-badge";
import XIcon from "../icon-old/x.svg";
import * as tagInputCss from "./tag-input.module.css";

interface TagLike {
  name: string;
  pillTheme: PillTheme;
}

// All 15 primary-type colors (light background), one entry per RedoBadgeColor,
// followed by all 15 alternate-type colors (dark background).
export const TagPillThemes: PillTheme[] = [
  // Primary (light) — one per RedoBadgeColor
  PillTheme.NORMAL,
  PillTheme.BRAND,
  PillTheme.SOLID_RED,
  PillTheme.NEUTRAL_YELLOW,
  PillTheme.SUCCESS,
  PillTheme.GRAY_BLUE,
  PillTheme.NEUTRAL_BLUE,
  PillTheme.SOLID_LIGHT_BLUE,
  PillTheme.SOLID_NAVY,
  PillTheme.PRIMARY_LIGHT,
  PillTheme.LIGHT_PINK,
  PillTheme.DANGER,
  PillTheme.SOLID_BLACK,
  // Alternate (dark)
  PillTheme.GRAY_ALTERNATE,
  PillTheme.BRAND_ALTERNATE,
  PillTheme.ERROR_ALTERNATE,
  PillTheme.WARNING_ALTERNATE,
  PillTheme.SUCCESS_ALTERNATE,
  PillTheme.GRAY_BLUE_ALTERNATE,
  PillTheme.BLUE_LIGHT_ALTERNATE,
  PillTheme.BLUE_ALTERNATE,
  PillTheme.INDIGO_ALTERNATE,
  PillTheme.PURPLE_ALTERNATE,
  PillTheme.PINK_ALTERNATE,
  PillTheme.ORANGE_ALTERNATE,
  PillTheme.BLACK_ALTERNATE,
];

export const BadgePillThemeToColorMapping: Record<PillTheme, RedoBadgeColor> = {
  // Legacy entries — kept for backward compat with stored data
  [PillTheme.NORMAL]: "gray",
  [PillTheme.PRIMARY_LIGHT]: "purple",
  [PillTheme.LIGHT_PINK]: "pink",
  [PillTheme.DANGER]: "orange",
  [PillTheme.NEUTRAL_YELLOW]: "warning",
  [PillTheme.SUCCESS]: "success",
  [PillTheme.NEUTRAL_BLUE]: "blueLight",
  [PillTheme.PRIMARY]: "purple",
  [PillTheme.SOLID_PINK]: "pink",
  [PillTheme.SOLID_RED]: "error",
  [PillTheme.SOLID_ORANGE]: "orange",
  [PillTheme.SOLID_YELLOW]: "warning",
  [PillTheme.SOLID_GREEN]: "success",
  [PillTheme.SOLID_LIGHT_BLUE]: "blue",
  [PillTheme.SOLID_NAVY]: "indigo",
  [PillTheme.SOLID_BLACK]: "black",
  [PillTheme.SOLID_WHITE]: "white",
  // New primary-type entries
  [PillTheme.BRAND]: "brand",
  [PillTheme.GRAY_BLUE]: "grayBlue",
  [PillTheme.WHITE_SUCCESS]: "whiteSuccess",
  // New alternate-type entries
  [PillTheme.GRAY_ALTERNATE]: "gray",
  [PillTheme.BRAND_ALTERNATE]: "brand",
  [PillTheme.ERROR_ALTERNATE]: "error",
  [PillTheme.WARNING_ALTERNATE]: "warning",
  [PillTheme.SUCCESS_ALTERNATE]: "success",
  [PillTheme.GRAY_BLUE_ALTERNATE]: "grayBlue",
  [PillTheme.BLUE_LIGHT_ALTERNATE]: "blueLight",
  [PillTheme.BLUE_ALTERNATE]: "blue",
  [PillTheme.INDIGO_ALTERNATE]: "indigo",
  [PillTheme.PURPLE_ALTERNATE]: "purple",
  [PillTheme.PINK_ALTERNATE]: "pink",
  [PillTheme.ORANGE_ALTERNATE]: "orange",
  [PillTheme.BLACK_ALTERNATE]: "black",
  [PillTheme.WHITE_ALTERNATE]: "white",
  [PillTheme.WHITE_SUCCESS_ALTERNATE]: "whiteSuccess",
};

export const BadgePillThemeToTypeMapping: Record<PillTheme, RedoBadgeType> = {
  // All legacy entries use primary type
  [PillTheme.NORMAL]: "primary",
  [PillTheme.PRIMARY_LIGHT]: "primary",
  [PillTheme.LIGHT_PINK]: "primary",
  [PillTheme.DANGER]: "primary",
  [PillTheme.NEUTRAL_YELLOW]: "primary",
  [PillTheme.SUCCESS]: "primary",
  [PillTheme.NEUTRAL_BLUE]: "primary",
  [PillTheme.PRIMARY]: "primary",
  [PillTheme.SOLID_PINK]: "primary",
  [PillTheme.SOLID_RED]: "primary",
  [PillTheme.SOLID_ORANGE]: "primary",
  [PillTheme.SOLID_YELLOW]: "primary",
  [PillTheme.SOLID_GREEN]: "primary",
  [PillTheme.SOLID_LIGHT_BLUE]: "primary",
  [PillTheme.SOLID_NAVY]: "primary",
  [PillTheme.SOLID_BLACK]: "primary",
  [PillTheme.SOLID_WHITE]: "primary",
  // New primary-type entries
  [PillTheme.BRAND]: "primary",
  [PillTheme.GRAY_BLUE]: "primary",
  [PillTheme.WHITE_SUCCESS]: "primary",
  // New alternate-type entries
  [PillTheme.GRAY_ALTERNATE]: "alternate",
  [PillTheme.BRAND_ALTERNATE]: "alternate",
  [PillTheme.ERROR_ALTERNATE]: "alternate",
  [PillTheme.WARNING_ALTERNATE]: "alternate",
  [PillTheme.SUCCESS_ALTERNATE]: "alternate",
  [PillTheme.GRAY_BLUE_ALTERNATE]: "alternate",
  [PillTheme.BLUE_LIGHT_ALTERNATE]: "alternate",
  [PillTheme.BLUE_ALTERNATE]: "alternate",
  [PillTheme.INDIGO_ALTERNATE]: "alternate",
  [PillTheme.PURPLE_ALTERNATE]: "alternate",
  [PillTheme.PINK_ALTERNATE]: "alternate",
  [PillTheme.ORANGE_ALTERNATE]: "alternate",
  [PillTheme.BLACK_ALTERNATE]: "alternate",
  [PillTheme.WHITE_ALTERNATE]: "alternate",
  [PillTheme.WHITE_SUCCESS_ALTERNATE]: "alternate",
};

export const TagPill = memo(function TagPill({
  tag,
  xClicked,
  showOverflow = false,
  badgeSize = "sm",
}: {
  tag: TagLike;
  xClicked?(): void;
  showOverflow?: boolean;
  badgeSize?: RedoBadgeSize;
}) {
  return (
    <div
      className={classNames(
        tagInputCss.pillWrapper,
        showOverflow && tagInputCss.showOverflow,
      )}
    >
      <RedoBadge
        color={BadgePillThemeToColorMapping[tag.pillTheme]}
        iconTrailing={
          xClicked
            ? { type: "icon", onClick: xClicked, Icon: XIcon }
            : undefined
        }
        size={badgeSize}
        text={tag.name}
        type={BadgePillThemeToTypeMapping[tag.pillTheme]}
      />
    </div>
  );
});
